import { View } from "react-native";
import isURL from "validator/lib/isURL";
import { useReceiptOrder } from "../../ReceiptContext";
import { VendorMetadataBlockSettings } from "../../../../../../web-receipt-api/src/lib/validateReceiptStructure";
import { cssStyles } from "../ReceiptFooter/styles";
import { VendorMetadataEntry, vendorEntriesByType } from "../../utils";

export type VendorMetadataBlockProps = {
  blockSettings: VendorMetadataBlockSettings & {
    useMonospace?: boolean;
  };
};

const isUrl = (value: string): boolean =>
  isURL(value, { require_protocol: true, protocols: ["http", "https"] });

/**
 * Renders vendor metadata entries as a comma-separated inline list, turning
 * any `http(s)` value into a link and leaving other values as plain text.
 *
 * @example
 * renderMetadataList([
 *   { vendor: "invoice-express", value: "https://app.invoicexpress.com/documents/123" },
 *   { vendor: "acme", value: "PT123456789" },
 * ])
 * // → <a href="https://app.invoicexpress.com/documents/123">https://…</a>, PT123456789
 */
function renderMetadataList(entries: VendorMetadataEntry[]) {
  return entries.map((entry, index) => (
    <span key={index}>
      {index > 0 && ", "}
      {isUrl(entry.value) ? (
        <a href={entry.value} target="_blank" rel="noopener noreferrer">
          {entry.value}
        </a>
      ) : (
        entry.value
      )}
    </span>
  ));
}

export function VendorMetadataBlock(props: VendorMetadataBlockProps) {
  const order = useReceiptOrder();
  const { metadataType, useMonospace = false } = props.blockSettings;

  const entries = vendorEntriesByType(order.vendorMetadata, metadataType);

  if (entries.length === 0) {
    return null;
  }

  return (
    <View>
      <div
        style={{ padding: "8px" }}
        data-vendors={entries.map((entry) => entry.vendor).join(",")}
      >
        <div
          style={{
            ...cssStyles.extraText,
            ...(useMonospace && { fontFamily: "monospace" }),
          }}
        >
          {renderMetadataList(entries)}
        </div>
      </div>
    </View>
  );
}
